import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const ContactContainer = styled.div`
  min-height: calc(100vh - 200px);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 2rem 0;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #efb348;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ContactCard = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: white;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .anticon {
    margin-bottom: 1rem;
    color: #efb348;
    font-size: 3rem;
  }

  h3 {
    margin-bottom: 1rem;
    color: #efb348;
    font-size: 1.3rem;
  }

  p {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
  }

  a {
    color: #42a5f5;
    text-decoration: none;

    &:hover {
      color: #81d4fa;
      text-decoration: underline;
    }
  }
`;

const ServiceSection = styled.section`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;

  h2 {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    color: #efb348;
    font-size: 1.8rem;
    text-align: center;
    border-bottom: 2px solid #efb348;
  }

  .service-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-top: 2rem;
  }

  .service-item {
    padding: 1.5rem;
    text-align: center;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;

    h4 {
      margin-bottom: 1rem;
      color: #efb348;
    }

    p {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
      line-height: 1.5;
    }
  }
`;

const NoticeSection = styled.section`
  background: rgba(239, 179, 72, 0.1);
  border: 1px solid #efb348;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: white;

  h3 {
    margin-bottom: 1rem;
    color: #efb348;
  }

  p {
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
  }

  .highlight {
    color: #efb348;
    font-weight: bold;
  }
`;

const Contact = () => {
  const { t } = useTranslation();

  return (
    <ContactContainer>
      <ContentWrapper>
        <PageTitle>{t('contact.title')}</PageTitle>

        <ContactGrid>
          <ContactCard>
            <MailOutlined />
            <h3>{t('contact.cards.email.title')}</h3>
            <p>{t('contact.cards.email.description')}</p>
            <p>
              <a href="mailto:support@sydgoldx.com.au">
                support@sydgoldx.com.au
              </a>
            </p>
          </ContactCard>

          <ContactCard>
            <EnvironmentOutlined />
            <h3>{t('contact.cards.address.title')}</h3>
            <p>{t('contact.cards.address.line1')}</p>
            <p>{t('contact.cards.address.line2')}</p>
            <p>{t('contact.cards.address.line3')}</p>
          </ContactCard>

          <ContactCard>
            <ClockCircleOutlined />
            <h3>{t('contact.cards.hours.title')}</h3>
            <p>{t('contact.cards.hours.line1')}</p>
            <p>{t('contact.cards.hours.line2')}</p>
            <p>{t('contact.cards.hours.line3')}</p>
          </ContactCard>
        </ContactGrid>

        <ServiceSection>
          <h2>{t('contact.registration.title')}</h2>
          <p
            style={{
              textAlign: 'center',
              marginBottom: '2rem',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            {t('contact.registration.description')}
          </p>

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <a
              href="https://form.jotform.com/221240572282044"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                transition: 'transform 0.3s ease',
              }}
              onMouseOver={e => (e.target.style.transform = 'scale(1.1)')}
              onMouseOut={e => (e.target.style.transform = 'scale(1)')}
            >
              <img
                src="/images/click-icon.png"
                width={64}
                alt={t('contact.registration.title')}
                style={{ cursor: 'pointer' }}
              />
            </a>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '1rem',
              }}
            >
              {t('contact.registration.assistance')}
            </p>
            <img
              src="/images/tunion-qr.jpeg"
              width={200}
              alt="Contact QR Code"
              style={{ borderRadius: '8px', marginBottom: '1rem' }}
            />
            <p
              style={{
                color: '#efb348',
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {t('contact.registration.mobile')}
            </p>
          </div>
        </ServiceSection>

        <ServiceSection>
          <h2>{t('contact.services.title')}</h2>
          <p
            style={{
              textAlign: 'center',
              marginBottom: '2rem',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            {t('contact.services.description')}
          </p>

          <div className="service-grid">
            <div className="service-item">
              <h4>{t('contact.services.items.exchange.title')}</h4>
              <p>{t('contact.services.items.exchange.description')}</p>
            </div>

            <div className="service-item">
              <h4>{t('contact.services.items.payment.title')}</h4>
              <p>{t('contact.services.items.payment.description')}</p>
            </div>

            <div className="service-item">
              <h4>{t('contact.services.items.settlement.title')}</h4>
              <p>{t('contact.services.items.settlement.description')}</p>
            </div>

            <div className="service-item">
              <h4>{t('contact.services.items.security.title')}</h4>
              <p>{t('contact.services.items.security.description')}</p>
            </div>
          </div>
        </ServiceSection>

        <NoticeSection>
          <h3>{t('contact.notice.title')}</h3>
          <p>
            {t('contact.notice.line1')
              .split(t('contact.notice.highlight1'))
              .map((part, index) =>
                index === 0 ? (
                  part
                ) : (
                  <React.Fragment key={index}>
                    <span className="highlight">
                      {t('contact.notice.highlight1')}
                    </span>
                    {part}
                  </React.Fragment>
                ),
              )}
          </p>
          <p>
            {t('contact.notice.line2')
              .split(t('contact.notice.highlight2'))
              .map((part, index) =>
                index === 0 ? (
                  part
                ) : (
                  <React.Fragment key={index}>
                    <span className="highlight">
                      {t('contact.notice.highlight2')}
                    </span>
                    {part}
                  </React.Fragment>
                ),
              )}
          </p>
          <p>
            {t('contact.notice.line3')
              .split(t('contact.notice.highlight3'))
              .map((part, index) =>
                index === 0 ? (
                  part
                ) : (
                  <React.Fragment key={index}>
                    <span className="highlight">
                      {t('contact.notice.highlight3')}
                    </span>
                    {part}
                  </React.Fragment>
                ),
              )}
          </p>
        </NoticeSection>
      </ContentWrapper>
    </ContactContainer>
  );
};

export default Contact;
